import React, { useEffect, useState } from 'react';
import { HelpCircle, PlayCircle, Download, X, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { CarouselTour, tourStorageKey } from './CarouselTour';
import { toast } from 'sonner';

/**
 * Floating help launcher — offers two things:
 *   1. Replay the interactive dashboard tour for the user's role.
 *   2. Download the branded PDF training manual for that role.
 *
 * Auto-opens the tour the first time a signed-in user lands on any
 * dashboard route (persisted per-user in localStorage).
 */
export function HelpLauncher() {
  const { user, api } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [tourSteps, setTourSteps] = useState([]);
  const [loadingSteps, setLoadingSteps] = useState(false);

  const role = user?.role || null;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Fetch screenshot-based tour manifest whenever the user changes.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoadingSteps(true);
    api.get('/api/tour/manifest')
      .then(({ data }) => {
        if (cancelled) return;
        const steps = (data?.steps || []).map((s) => ({
          ...s,
          image: s.image?.startsWith('http') ? s.image : `${backendUrl}${s.image}`,
        }));
        setTourSteps(steps);
      })
      .catch(() => { if (!cancelled) setTourSteps([]); })
      .finally(() => { if (!cancelled) setLoadingSteps(false); });
    return () => { cancelled = true; };
  }, [user, api, backendUrl]);

  // Auto-launch the tour once per user per role.
  useEffect(() => {
    if (!user || !tourSteps.length) return;
    const key = tourStorageKey(user.id, role);
    const seen = typeof localStorage !== 'undefined' && localStorage.getItem(key);
    if (!seen) {
      const t = setTimeout(() => setTourOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, [user, role, tourSteps.length]);

  const startTour = () => {
    setMenuOpen(false);
    setTourOpen(true);
  };

  const downloadManual = async () => {
    if (!role) return;
    setDownloading(true);
    try {
      const res = await api.get(`/api/training/manual/${role}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AGA-${role}-training-manual.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Training manual downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not download the manual');
    } finally {
      setDownloading(false);
      setMenuOpen(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <div className="fixed bottom-6 right-6 z-[60]" data-testid="help-launcher">
        {menuOpen && (
          <div className="mb-3 w-64 rounded-2xl bg-[#041A3B] border border-white/10 shadow-2xl p-2 keep-dark animate-in fade-in slide-in-from-bottom-2" role="menu">
            <div className="px-3 py-2 flex items-center justify-between">
              <span className="text-white text-sm font-semibold">Need a hand?</span>
              <button onClick={() => setMenuOpen(false)} className="text-[#A0B4CC] hover:text-white" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={startTour}
              disabled={loadingSteps || tourSteps.length === 0}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-white/5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="help-start-tour"
            >
              {loadingSteps
                ? <Loader2 className="w-5 h-5 text-[#00E5FF] animate-spin flex-shrink-0" />
                : <PlayCircle className="w-5 h-5 text-[#00E5FF] flex-shrink-0" />}
              <div className="min-w-0">
                <div className="text-white text-sm">Replay Dashboard Tour</div>
                <div className="text-[#5C7599] text-xs">
                  {tourSteps.length > 0 ? `${tourSteps.length} guided screens of your dashboard.` : 'Tour will be available soon.'}
                </div>
              </div>
            </button>
            <button
              onClick={downloadManual}
              disabled={downloading}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-white/5 transition-colors disabled:opacity-60"
              data-testid="help-download-manual"
            >
              {downloading ? (
                <Loader2 className="w-5 h-5 text-[#FFB800] animate-spin flex-shrink-0" />
              ) : (
                <Download className="w-5 h-5 text-[#FFB800] flex-shrink-0" />
              )}
              <div className="min-w-0">
                <div className="text-white text-sm">Download Training Manual</div>
                <div className="text-[#5C7599] text-xs">PDF guide tailored to your role.</div>
              </div>
            </button>
          </div>
        )}

        <button
          onClick={() => setMenuOpen((v) => !v)}
          data-testid="help-launcher-btn"
          className="w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br from-[#00E5FF] to-[#007AFF] text-[#000B18] shadow-lg shadow-[#00E5FF]/40 hover:shadow-[#00E5FF]/70 hover:scale-105 transition-all"
          aria-label="Help & Training"
        >
          <HelpCircle className="w-6 h-6" strokeWidth={2.4} />
        </button>
      </div>

      {tourSteps.length > 0 && (
        <CarouselTour
          steps={tourSteps}
          open={tourOpen}
          onClose={() => setTourOpen(false)}
          onFinish={() => toast.success('Tour complete — you\'re all set!')}
          storageKey={tourStorageKey(user.id, role)}
        />
      )}
    </>
  );
}

export default HelpLauncher;
